import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { Calendar, ArrowRight, CheckCircle, ListChecks, Play, Plus, AlertCircle, Info } from 'lucide-react'
import toast from 'react-hot-toast'

export interface SchoolYear {
  id: number
  name: string
  start_date: string
  end_date: string
  created_at?: string
  updated_at?: string
}

/** Ein Übergangsvorschlag aus suggestions-batch (mit Anzeigenamen) */
export interface TransitionSuggestion {
  student_id: number
  student_name: string
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  to_unit_type: string
  to_unit_id: number
  to_unit_name: string
  current_membership_id: number
  successor_id?: number
  notes?: string
}

/** Wiederholer-Kandidat: Schüler bleibt in gleicher Einheit */
export interface RepeaterCandidateStudent {
  student_id: number
  student_name: string
  current_membership_id: number
}

export interface RepeaterCandidateGroup {
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  students: RepeaterCandidateStudent[]
}

/** Ein Vorschlag für Massen-Nachfolger (successors-bulk-suggest) */
export interface BulkSuccessorSuggestion {
  from_id: number
  from_name: string
  to_id: number | null
  to_name: string
  already_exists: boolean
}

const STEPS = [
  {
    id: 'date',
    title: 'Schuljahr & Stichtag',
    description: 'Das Ziel-Schuljahr und der Stichtag werden von Lernrakete automatisch ermittelt.',
    icon: Calendar
  },
  {
    id: 'successors',
    title: 'Nachfolger prüfen',
    description: 'Folgebeziehungen (z. B. 07a→08a) prüfen oder in Masse anlegen.',
    icon: ListChecks
  },
  {
    id: 'preview',
    title: 'Vorschau',
    description: 'Übergänge und Wiederholer lesbar prüfen, Anpassungen vornehmen.',
    icon: CheckCircle
  },
  {
    id: 'execute',
    title: 'Ausführen',
    description: 'Schuljahreswechsel durchführen und Zusammenfassung anzeigen.',
    icon: Play
  }
]

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('de-DE')
}

function formatPeriodName(name?: string): string {
  if (!name) return '-'
  const readable = name.replace('1.HJ', '1. Halbjahr').replace('2.HJ', '2. Halbjahr')
  return readable.replace(' 1. Halbjahr', ' - 1. Halbjahr').replace(' 2. Halbjahr', ' - 2. Halbjahr')
}

function getHalfYearLabel(name?: string): string {
  if (!name) return 'nächstes Halbjahr'
  if (name.includes('1.HJ')) return '1. Halbjahr'
  if (name.includes('2.HJ')) return '2. Halbjahr'
  return 'nächstes Halbjahr'
}

function TermHint({ term, meaning }: { term: string; meaning: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {term}
      <span title={meaning} className="text-gray-400 hover:text-gray-600 cursor-help">
        <Info className="h-4 w-4" />
      </span>
    </span>
  )
}

export default function SchoolYearTransitionTab() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedSchoolYearId, setSelectedSchoolYearId] = useState<number | ''>('')
  const [transitionDate, setTransitionDate] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newStart, setNewStart] = useState('')
  const [newEnd, setNewEnd] = useState('')
  /** Ausgewählte Übergänge für die Ausführung (current_membership_id) */
  const [selectedTransitionIds, setSelectedTransitionIds] = useState<Set<number>>(new Set())
  /** Ausgewählte Wiederholer (current_membership_id) */
  const [selectedRepeaterIds, setSelectedRepeaterIds] = useState<Set<number>>(new Set())
  const [executeResult, setExecuteResult] = useState<{ created: number; ended: number; repeatersProcessed?: number; errors: unknown[] } | null>(null)
  const previewInitializedRef = useRef(false)
  const [bulkUnitType, setBulkUnitType] = useState<'class' | 'course' | 'group'>('class')
  const [bulkFromPrefix, setBulkFromPrefix] = useState('')
  const [bulkToPrefix, setBulkToPrefix] = useState('')
  const [bulkSuggestions, setBulkSuggestions] = useState<BulkSuccessorSuggestion[]>([])
  /** Welche Zeilen für die Anlage ausgewählt sind (Indizes in bulkSuggestions) */
  const [selectedBulkIndices, setSelectedBulkIndices] = useState<Set<number>>(new Set())
  /** Manuelle Überschreibung: Index → to_id (für "Nach"-Dropdown) */
  const [bulkToIdOverride, setBulkToIdOverride] = useState<Record<number, number | null>>({})
  const [bulkAutoLoading, setBulkAutoLoading] = useState(false)

  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = user?.role === 'administrator' || user?.role === 'admin'

  const { data: schoolYearsResponse, isLoading: schoolYearsLoading, error: schoolYearsError } = useQuery(
    ['school-years'],
    () => api.get<{ success: boolean; data: SchoolYear[] }>('/school-years').then(res => res.data),
    { staleTime: 60_000 }
  )
  const schoolYears = schoolYearsResponse?.data ?? []

  const { data: currentPeriodResponse } = useQuery(
    ['school-years', 'current'],
    () => api.get<{ success: boolean; data: { name: string; startDate: string; endDate: string } }>('/school-years/current').then(res => res.data),
    { staleTime: 60_000 }
  )
  const currentPeriod = currentPeriodResponse?.data

  const { data: switchSuggestionResponse } = useQuery(
    ['school-years', 'switch-suggestion'],
    () => api.get<{ success: boolean; data: { suggestSwitch: boolean; reason: string | null; periodName: string; nextPeriod: SchoolYear | null } }>('/school-years/switch-suggestion').then(res => res.data),
    { staleTime: 60_000, enabled: isAdmin }
  )
  const switchSuggestion = switchSuggestionResponse?.data

  const { data: transitionTargetResponse } = useQuery(
    ['school-years', 'transition-target'],
    () => api.get<{ success: boolean; data: { schoolYear: SchoolYear | null; transition_date: string; periodName: string } }>('/school-years/transition-target').then(res => res.data),
    { staleTime: 60_000 }
  )
  const transitionTarget = transitionTargetResponse?.data

  const ensurePeriodsMutation = useMutation(
    () => api.post<{ success: boolean; data: SchoolYear[]; generated: number; fromYear: number; toYear: number }>('/school-years/ensure-periods').then(res => res.data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['school-years'])
        queryClient.invalidateQueries(['school-years', 'transition-target'])
        toast.success(`${data.generated} Schuljahre (Halbjahre) erzeugt: ${data.fromYear}–${data.toYear}.`)
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || 'Schuljahre konnten nicht erzeugt werden.')
      }
    }
  )

  const createSchoolYearMutation = useMutation(
    (body: { name: string; start_date: string; end_date: string }) =>
      api.post('/school-years', body).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['school-years'])
        setNewName('')
        setNewStart('')
        setNewEnd('')
        setShowAddForm(false)
        toast.success('Schuljahr angelegt')
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || 'Schuljahr konnte nicht angelegt werden')
      }
    }
  )

  const { data: batchSuggestionsResponse, isLoading: batchSuggestionsLoading, error: batchSuggestionsError } = useQuery(
    ['transitions-suggestions-batch', transitionDate],
    () => api.get<{ success: boolean; data: { suggestions: TransitionSuggestion[]; repeater_candidates: RepeaterCandidateGroup[] } }>('/transitions/suggestions-batch', { params: { transition_date: transitionDate } }).then(res => res.data),
    { enabled: currentStep >= 2 && !!transitionDate.trim(), staleTime: 30_000 }
  )
  const batchSuggestions = batchSuggestionsResponse?.data?.suggestions ?? []
  const repeaterCandidates = batchSuggestionsResponse?.data?.repeater_candidates ?? []

  const { data: successorHealth, isLoading: successorHealthLoading, error: successorHealthError } = useQuery(
    ['transitions-successor-health'],
    async () => {
      const [classesRes, coursesRes] = await Promise.all([
        api.get<{ success: boolean; data: Array<{ id: number; active_members_count?: number }> }>('/units/class', { params: { is_active: true } }),
        api.get<{ success: boolean; data: Array<{ id: number; active_members_count?: number }> }>('/units/course', { params: { is_active: true } })
      ])

      const classes = classesRes.data?.data ?? []
      const courses = coursesRes.data?.data ?? []
      const relevantClasses = classes.filter((u) => (u.active_members_count ?? 0) > 0)
      const relevantCourses = courses.filter((u) => (u.active_members_count ?? 0) > 0)

      const classMissingResults = await Promise.all(
        relevantClasses.map(async (unit) => {
          const res = await api.get<{ success: boolean; data: unknown[] }>(`/units/class/${unit.id}/successors`)
          return (res.data?.data?.length ?? 0) === 0
        })
      )
      const courseMissingResults = await Promise.all(
        relevantCourses.map(async (unit) => {
          const res = await api.get<{ success: boolean; data: unknown[] }>(`/units/course/${unit.id}/successors`)
          return (res.data?.data?.length ?? 0) === 0
        })
      )

      const classesWithoutSuccessor = classMissingResults.filter(Boolean).length
      const coursesWithoutSuccessor = courseMissingResults.filter(Boolean).length
      const missingTotal = classesWithoutSuccessor + coursesWithoutSuccessor

      return {
        classesWithoutSuccessor,
        coursesWithoutSuccessor,
        missingTotal
      }
    },
    { staleTime: 60_000, enabled: currentStep >= 1 }
  )

  useEffect(() => {
    if (currentStep !== 2) previewInitializedRef.current = false
  }, [currentStep])
  useEffect(() => {
    if (currentStep === 2 && batchSuggestions.length > 0 && !previewInitializedRef.current) {
      setSelectedTransitionIds(new Set(batchSuggestions.map((s: TransitionSuggestion) => s.current_membership_id)))
      setSelectedRepeaterIds(new Set())
      previewInitializedRef.current = true
    }
  }, [currentStep, batchSuggestions])

  const toggleTransitionSelection = (membershipId: number) => {
    setSelectedTransitionIds(prev => {
      const next = new Set(prev)
      if (next.has(membershipId)) next.delete(membershipId)
      else next.add(membershipId)
      return next
    })
  }

  const selectAllTransitions = () => setSelectedTransitionIds(new Set(batchSuggestions.map((s: TransitionSuggestion) => s.current_membership_id)))
  const deselectAllTransitions = () => setSelectedTransitionIds(new Set())

  const executeBatchMutation = useMutation(
    (payload: { transition_date: string; transitions: unknown[]; repeaters?: unknown[]; school_year_id?: number | null }) =>
      api.post('/transitions/execute-batch', payload).then(res => res.data),
    {
      onSuccess: (data: { success: boolean; data: { created: number; ended: number; repeatersProcessed?: number; errors: unknown[] } }) => {
        setExecuteResult(data.data)
        queryClient.invalidateQueries(['transitions-suggestions-batch'])
        queryClient.invalidateQueries(['class'])
        queryClient.invalidateQueries(['course'])
        queryClient.invalidateQueries(['group'])
        queryClient.invalidateQueries(['memberships'])
        const msg = data.data.repeatersProcessed
          ? `${data.data.created} Übergänge, ${data.data.repeatersProcessed} Wiederholer`
          : `${data.data.created} Übergänge`
        if (data.success) toast.success(msg + ' durchgeführt')
        else toast.error('Ausführung mit Fehlern beendet')
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || 'Schuljahreswechsel fehlgeschlagen')
      }
    }
  )

  const selectedTransitionsForExecute = batchSuggestions.filter((s: TransitionSuggestion) => selectedTransitionIds.has(s.current_membership_id)).map((s: TransitionSuggestion) => ({
    current_membership_id: s.current_membership_id,
    student_id: s.student_id,
    from_unit_type: s.from_unit_type,
    from_unit_id: s.from_unit_id,
    to_unit_type: s.to_unit_type,
    to_unit_id: s.to_unit_id,
    notes: s.notes
  }))

  const toggleRepeaterSelection = (membershipId: number) => {
    setSelectedRepeaterIds(prev => {
      const next = new Set(prev)
      if (next.has(membershipId)) next.delete(membershipId)
      else next.add(membershipId)
      return next
    })
  }

  const selectedRepeatersForExecute = repeaterCandidates.flatMap(rc =>
    rc.students
      .filter((st: RepeaterCandidateStudent) => selectedRepeaterIds.has(st.current_membership_id))
      .map((st: RepeaterCandidateStudent) => ({
        student_id: st.student_id,
        unit_type: rc.from_unit_type,
        unit_id: rc.from_unit_id,
        current_membership_id: st.current_membership_id
      }))
  )

  const applyBulkSuggestions = (list: BulkSuccessorSuggestion[]) => {
    setBulkSuggestions(list)
    setBulkToIdOverride({})
    const creatable = new Set(list.map((s, i) => (s.to_id != null && !s.already_exists ? i : -1)).filter(i => i >= 0))
    setSelectedBulkIndices(creatable)
  }

  const fetchAutoSuggest = async () => {
    setBulkAutoLoading(true)
    try {
      const res = await api.get<{ success: boolean; data: BulkSuccessorSuggestion[] }>('/units/successors-auto-suggest', {
        params: { unit_type: bulkUnitType }
      })
      applyBulkSuggestions(res.data?.data ?? [])
    } finally {
      setBulkAutoLoading(false)
    }
  }

  const fetchBulkSuggestions = async () => {
    if (!bulkFromPrefix.trim() || !bulkToPrefix.trim()) return
    const res = await api.get<{ success: boolean; data: BulkSuccessorSuggestion[] }>('/units/successors-bulk-suggest', {
      params: { unit_type: bulkUnitType, from_prefix: bulkFromPrefix.trim(), to_prefix: bulkToPrefix.trim() }
    })
    applyBulkSuggestions(res.data?.data ?? [])
  }

  const { data: bulkUnitsData } = useQuery(
    ['units-list', bulkUnitType],
    () => api.get(`/units/${bulkUnitType}`, { params: { is_active: 'true' } }).then(res => res.data),
    { enabled: bulkSuggestions.length > 0, staleTime: 60_000 }
  )
  const bulkUnitsList = (bulkUnitsData?.data ?? []) as { id: number; class_name?: string; name?: string }[]
  const bulkUnitNameKey = bulkUnitType === 'class' ? 'class_name' : 'name'

  const bulkCreateMutation = useMutation(
    (pairs: { from_id: number; to_id: number }[]) =>
      api.post('/units/successors-bulk', { unit_type: bulkUnitType, pairs }).then(res => res.data),
    {
      onSuccess: (data: { data: { created: number; skipped: number } }) => {
        toast.success(`${data.data.created} Nachfolger angelegt${data.data.skipped ? `, ${data.data.skipped} übersprungen (bereits vorhanden)` : ''}`)
        fetchAutoSuggest()
        queryClient.invalidateQueries(['transitions-suggestions-batch'])
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || 'Nachfolger konnten nicht angelegt werden')
      }
    }
  )

  const creatableBulkIndices = useMemo(
    () => bulkSuggestions
      .map((s, i) => i)
      .filter(i => !bulkSuggestions[i].already_exists && (bulkSuggestions[i].to_id != null || bulkToIdOverride[i] != null)),
    [bulkSuggestions, bulkToIdOverride]
  )
  const bulkPairsToCreate = useMemo(() => {
    return Array.from(selectedBulkIndices)
      .map(i => {
        const s = bulkSuggestions[i]
        if (!s || s.already_exists) return null
        const toId = bulkToIdOverride[i] ?? s.to_id
        if (toId == null) return null
        return { from_id: s.from_id, to_id: toId }
      })
      .filter((p): p is { from_id: number; to_id: number } => p != null)
  }, [bulkSuggestions, selectedBulkIndices, bulkToIdOverride])

  const selectAllBulk = () => setSelectedBulkIndices(new Set(creatableBulkIndices))
  const deselectAllBulk = () => setSelectedBulkIndices(new Set())
  const toggleBulkRow = (index: number) => {
    if (bulkSuggestions[index].already_exists) return
    setSelectedBulkIndices(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  useEffect(() => {
    if (!transitionTarget?.transition_date) return
    setTransitionDate(transitionTarget.transition_date)
    if (transitionTarget.schoolYear?.id != null) {
      setSelectedSchoolYearId(transitionTarget.schoolYear.id)
    }
  }, [transitionTarget?.transition_date, transitionTarget?.schoolYear?.id])

  const selectedSchoolYear = schoolYears.find((y: SchoolYear) => y.id === selectedSchoolYearId) ?? transitionTarget?.schoolYear ?? null
  const canProceedFromStep1 = transitionDate.trim() !== ''
  const hasStep1SetupIssue = Boolean(schoolYearsError) || schoolYears.length === 0 || !transitionTarget?.schoolYear
  const nextPeriodLabel = formatPeriodName(transitionTarget?.periodName)
  const currentPeriodLabel = formatPeriodName(currentPeriod?.name)
  const topStatusText = switchSuggestion?.suggestSwitch
    ? `Es ist Zeit für den Schuljahreswechsel (${getHalfYearLabel(transitionTarget?.periodName || switchSuggestion.periodName)}).`
    : 'Schuljahreswechsel kann vorbereitet werden.'

  const successorStatus = useMemo(() => {
    if (successorHealthLoading) {
      return { tone: 'neutral', label: 'Status wird geprüft…' }
    }
    if (successorHealthError) {
      return { tone: 'warning', label: 'Status unvollständig – Nachfolger bitte manuell prüfen.' }
    }
    const missing = successorHealth?.missingTotal ?? 0
    if (missing === 0) return { tone: 'ok', label: 'Alles bereit: Alle relevanten Einheiten haben einen Nachfolger.' }
    if (missing <= 5) return { tone: 'warning', label: `${missing} Einheiten ohne Nachfolger.` }
    return { tone: 'blocking', label: `${missing} Einheiten ohne Nachfolger (blockierend).` }
  }, [successorHealthLoading, successorHealthError, successorHealth?.missingTotal])

  const successorStatusClass = successorStatus.tone === 'ok'
    ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200'
    : successorStatus.tone === 'blocking'
      ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200'
      : successorStatus.tone === 'warning'
        ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
        : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-950/80 dark:text-gray-300'

  return (
    <div className="card p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Zentraler Schuljahreswechsel</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Ein Ablauf für die ganze Schule: alle Klassen und Kurse, Übergänge und Wiederholer an einem Ort.
      </p>

      <div className={`mt-4 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between ${
        switchSuggestion?.suggestSwitch
          ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
          : 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40'
      }`}>
        <div className="flex items-start gap-3">
          <AlertCircle className={`h-5 w-5 shrink-0 ${switchSuggestion?.suggestSwitch ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} />
          <div>
            <p className={`text-sm font-medium ${switchSuggestion?.suggestSwitch ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200'}`}>
              {topStatusText}
            </p>
            {switchSuggestion?.reason && (
              <p className={`mt-1 text-sm ${switchSuggestion?.suggestSwitch ? 'text-amber-700 dark:text-amber-300' : 'text-blue-700 dark:text-blue-300'}`}>
                {switchSuggestion.reason}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCurrentStep(0)}
          className="btn btn-primary w-full sm:w-auto"
        >
          Schuljahreswechsel starten
        </button>
      </div>

      {/* Stepper */}
      <nav aria-label="Fortschritt" className="mt-6">
        <ol className="flex flex-wrap gap-2 sm:gap-4">
          {STEPS.map((step, index) => {
            const Icon = step.icon
            const isActive = index === currentStep
            const isPast = index < currentStep
            return (
              <li key={step.id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => setCurrentStep(index)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-950/40 dark:text-primary-300 dark:ring-primary-800'
                      : isPast
                        ? 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{step.title}</span>
                  {isPast && <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />}
                </button>
                {index < STEPS.length - 1 && (
                  <ArrowRight className="mx-1 h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600 sm:mx-2" aria-hidden />
                )}
              </li>
            )
          })}
        </ol>
      </nav>

      {/* Step content */}
      <div className="mt-6 card p-4 sm:p-6">
        {currentStep === 0 && (
          <>
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-950/50">
                <Calendar className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-gray-900 dark:text-gray-50">{STEPS[0].title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{STEPS[0].description}</p>
              </div>
            </div>
            <div className="mt-6 space-y-5">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-950/80">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Jetzt aktiv:</strong> {currentPeriodLabel}
                </p>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  <strong>Als Nächstes:</strong> {nextPeriodLabel} (ab {formatDate(transitionTarget?.transition_date)})
                </p>
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-medium text-gray-500 dark:text-gray-400">Details anzeigen</summary>
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    {currentPeriod && (
                      <p>
                        Aktueller Datumsbereich: {currentPeriod.startDate} – {currentPeriod.endDate}
                      </p>
                    )}
                    {selectedSchoolYear && (
                      <p>
                        Nächster Datumsbereich: {selectedSchoolYear.start_date} – {selectedSchoolYear.end_date}
                      </p>
                    )}
                    <p>
                      <TermHint term="Stichtag" meaning="Ab diesem Datum gelten die neuen Zuordnungen." />: {transitionDate || '-'}
                    </p>
                  </div>
                </details>
              </div>

              {schoolYearsLoading && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Daten werden geladen…</p>
              )}
              {!!schoolYearsError && (
                <p className="text-sm text-red-600">Schuljahre konnten nicht geladen werden. Bitte Datenbasis prüfen.</p>
              )}

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  disabled={!canProceedFromStep1}
                  className="btn btn-primary w-full sm:w-auto"
                >
                  Weiter
                </button>
              </div>

              {hasStep1SetupIssue && isAdmin && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 p-4">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Für den Wechsel fehlt noch ein gültiges Ziel-Schuljahr.</p>
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Diese Verwaltung ist nur im Fehlerfall nötig.</p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => ensurePeriodsMutation.mutate()}
                      disabled={ensurePeriodsMutation.isLoading}
                      className="btn btn-secondary w-full sm:w-auto"
                    >
                      {ensurePeriodsMutation.isLoading ? 'Wird erzeugt…' : 'Schuljahre auffüllen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddForm(!showAddForm)}
                      className="btn btn-secondary inline-flex items-center justify-center gap-1 w-full sm:w-auto"
                    >
                      <Plus className="h-4 w-4" />
                      Neues Schuljahr anlegen
                    </button>
                  </div>
                  {showAddForm && (
                    <div className="mt-4 card p-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-50 dark:text-gray-50">Neues Schuljahr anlegen</h4>
                      <div className="mt-3 flex flex-wrap gap-3">
                        <input
                          type="text"
                          placeholder="z. B. 2026/2027 1.HJ"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        <input
                          type="date"
                          value={newStart}
                          onChange={(e) => setNewStart(e.target.value)}
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        <input
                          type="date"
                          value={newEnd}
                          onChange={(e) => setNewEnd(e.target.value)}
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => createSchoolYearMutation.mutate({ name: newName.trim(), start_date: newStart, end_date: newEnd })}
                          disabled={!newName.trim() || !newStart || !newEnd || createSchoolYearMutation.isLoading}
                          className="btn btn-primary"
                        >
                          {createSchoolYearMutation.isLoading ? 'Wird angelegt…' : 'Anlegen'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        {/* Schritt 2: Nachfolger prüfen */}
        {currentStep === 1 && (
          <>
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary-100 p-2">
                <ListChecks className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-50">{STEPS[1].title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Prüfen Sie die <TermHint term="Nachfolger" meaning="Die nächste Klasse oder der nächste Kurs, in den Lernende wechseln." />. Ohne Nachfolger sind Übergänge nicht vollständig.
                </p>
              </div>
            </div>

            <div className={`mt-6 rounded-lg border p-4 ${successorStatusClass}`}>
              <p className="text-sm font-medium">
                {successorStatus.label}
              </p>
              {!successorHealthLoading && !successorHealthError && (successorHealth?.missingTotal ?? 0) > 0 && (
                <p className="mt-1 text-xs">
                  {successorHealth?.classesWithoutSuccessor ?? 0} Klassen ohne Nachfolger · {successorHealth?.coursesWithoutSuccessor ?? 0} Kurse ohne Nachfolger
                </p>
              )}
            </div>

            {isAdmin && (
              <details className="mt-6 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-50 dark:text-gray-50">Nachfolger in Masse anlegen</summary>
                <p className="mt-2 text-xs text-gray-500">
                  Das Programm ermittelt selbst: Einheiten ohne Nachfolger werden um 1 hochgezählt (05a→06a, 08e→09e). Typ wählen, Vorschläge laden, Zeilen an-/abwählen oder „Nach“ anpassen, dann „Nachfolger anlegen“.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Typ</label>
                    <select
                      value={bulkUnitType}
                      onChange={(e) => setBulkUnitType(e.target.value as 'class' | 'course' | 'group')}
                      className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="class">Klassen</option>
                      <option value="course">Kurse</option>
                      <option value="group">Gruppen</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => fetchAutoSuggest()}
                    disabled={bulkAutoLoading}
                    className="btn btn-primary"
                  >
                    {bulkAutoLoading ? 'Wird ermittelt…' : 'Vorschläge automatisch ermitteln'}
                  </button>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800">Manuell: Von/Nach angeben</summary>
                    <div className="mt-2 flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50/50 p-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600">Von (Präfix)</label>
                        <input
                          type="text"
                          placeholder="z. B. 07"
                          value={bulkFromPrefix}
                          onChange={(e) => setBulkFromPrefix(e.target.value)}
                          className="mt-1 w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600">Nach (Präfix)</label>
                        <input
                          type="text"
                          placeholder="z. B. 08"
                          value={bulkToPrefix}
                          onChange={(e) => setBulkToPrefix(e.target.value)}
                          className="mt-1 w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={fetchBulkSuggestions}
                        disabled={!bulkFromPrefix.trim() || !bulkToPrefix.trim()}
                        className="btn btn-secondary"
                      >
                        Vorschläge laden
                      </button>
                    </div>
                  </details>
                </div>
                {bulkSuggestions.length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <span>Einzelne Zeilen ein- oder abwählen, bei Bedarf „Nach“ anpassen.</span>
                      {creatableBulkIndices.length > 0 && (
                        <>
                          <button type="button" onClick={selectAllBulk} className="text-primary-600 hover:underline">Alle auswählen</button>
                          <span className="text-gray-300">|</span>
                          <button type="button" onClick={deselectAllBulk} className="text-primary-600 hover:underline">Alle abwählen</button>
                        </>
                      )}
                    </p>
                    <div className="max-h-56 overflow-auto rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 text-sm">
                      <table className="min-w-full">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="w-10 px-2 py-2 text-left font-medium text-gray-700">✓</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Von</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Nach</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkSuggestions.map((s, i) => {
                            const canSelect = !s.already_exists && (s.to_id != null || bulkToIdOverride[i] != null)
                            const isSelected = selectedBulkIndices.has(i)
                            const effectiveToId = bulkToIdOverride[i] ?? s.to_id
                            const effectiveToName = effectiveToId != null
                              ? (bulkUnitsList.find(u => u.id === effectiveToId)?.[bulkUnitNameKey] ?? s.to_name ?? `#${effectiveToId}`)
                              : null
                            return (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="px-2 py-2">
                                  {s.already_exists ? (
                                    <span className="text-gray-400">–</span>
                                  ) : (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleBulkRow(i)}
                                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2">{s.from_name}</td>
                                <td className="px-3 py-2">
                                  {s.already_exists ? (
                                    s.to_name
                                  ) : bulkUnitsList.length > 0 ? (
                                    <select
                                      value={effectiveToId ?? ''}
                                      onChange={e => {
                                        const v = e.target.value
                                        setBulkToIdOverride(prev => ({ ...prev, [i]: v === '' ? null : parseInt(v, 10) }))
                                      }}
                                      className="w-full min-w-[6rem] rounded border border-gray-300 py-1 pl-2 pr-6 text-sm"
                                    >
                                      <option value="">– nicht anlegen –</option>
                                      {bulkUnitsList.map(u => (
                                        <option key={u.id} value={u.id}>
                                          {u[bulkUnitNameKey] ?? u.name}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    s.to_id ? s.to_name : '–'
                                  )}
                                </td>
                                <td className="px-3 py-2">{s.already_exists ? 'bereits vorhanden' : canSelect ? (effectiveToId != null ? 'wird angelegt' : 'ausgewählt: –') : 'kein Vorschlag'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                    {bulkPairsToCreate.length > 0 && (
                      <button
                        type="button"
                        onClick={() => bulkCreateMutation.mutate(bulkPairsToCreate)}
                        disabled={bulkCreateMutation.isLoading}
                        className="mt-3 btn btn-primary"
                      >
                        {bulkCreateMutation.isLoading ? 'Wird angelegt…' : `${bulkPairsToCreate.length} Nachfolger anlegen`}
                      </button>
                    )}
                  </div>
                )}
              </details>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => setCurrentStep(0)} className="btn btn-secondary w-full sm:w-auto">← Zurück</button>
              {(successorHealth?.missingTotal ?? 0) > 0 ? (
                <button
                  type="button"
                  onClick={() => navigate('/units?type=class')}
                  className="btn btn-primary w-full sm:w-auto"
                >
                  Nachfolger jetzt ergänzen
                </button>
              ) : (
                <button type="button" onClick={() => setCurrentStep(2)} className="btn btn-primary w-full sm:w-auto">Weiter</button>
              )}
            </div>
          </>
        )}

        {/* Schritt 3: Vorschau */}
        {currentStep === 2 && (
          <>
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary-100 p-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-50">{STEPS[2].title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{STEPS[2].description}</p>
                <p className="mt-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40 px-3 py-2 text-sm text-blue-800 dark:text-blue-200">
                  Hier wird noch nichts geändert - bitte alles sorgfältig prüfen.
                </p>
              </div>
            </div>
            {!transitionDate.trim() && (
              <p className="mt-4 text-sm text-amber-700 dark:text-amber-300">Ziel und Stichtag werden geladen… Bitte kurz warten oder Schritt 1 prüfen.</p>
            )}
            {transitionDate.trim() && (
              <div className="mt-6">
                {batchSuggestionsLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Vorschläge werden geladen…</p>}
                {!!batchSuggestionsError && <p className="text-sm text-red-600">Vorschläge konnten nicht geladen werden.</p>}
                {!batchSuggestionsLoading && !batchSuggestionsError && batchSuggestions.length === 0 && repeaterCandidates.filter(rc => rc.students.length > 0).length === 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">Keine Übergangsvorschläge und keine Wiederholer-Kandidaten (keine Nachfolger hinterlegt oder keine aktiven Mitglieder).</p>
                )}
                {!batchSuggestionsLoading && !batchSuggestionsError && (batchSuggestions.length > 0 || repeaterCandidates.length > 0) && (
                  <>
                    <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                      <TermHint term="Stichtag" meaning="Ab diesem Datum gelten die neuen Zuordnungen." />: <strong>{transitionDate}</strong>
                      {batchSuggestions.length > 0 && (
                        <> · {selectedTransitionIds.size} von {batchSuggestions.length} <TermHint term="Übergängen" meaning="Wechsel von einer Klasse/Kurs in die nächste Einheit." /> ausgewählt</>
                      )}
                      {repeaterCandidates.some(rc => rc.students.length > 0) && (
                        <> · {selectedRepeaterIds.size} <TermHint term="Wiederholern" meaning="Lernende bleiben in der gleichen Klasse oder im gleichen Kurs." /> ausgewählt</>
                      )}
                    </p>
                    {batchSuggestions.length > 0 && (
                      <>
                        <div className="mb-2 flex gap-2">
                          <button type="button" onClick={selectAllTransitions} className="text-sm text-primary-600 hover:underline">Alle auswählen</button>
                          <span className="text-gray-300">|</span>
                          <button type="button" onClick={deselectAllTransitions} className="text-sm text-primary-600 hover:underline">Alle abwählen</button>
                        </div>
                        <div className="max-h-80 overflow-auto rounded-lg border border-gray-200">
                          <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 font-medium text-gray-700 w-10">✓</th>
                                <th className="px-3 py-2 font-medium text-gray-700">Von</th>
                                <th className="px-3 py-2 font-medium text-gray-700">Schüler:in</th>
                                <th className="px-3 py-2 font-medium text-gray-700">Nach</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
                              {batchSuggestions.map((s: TransitionSuggestion) => (
                                <tr key={s.current_membership_id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={selectedTransitionIds.has(s.current_membership_id)}
                                      onChange={() => toggleTransitionSelection(s.current_membership_id)}
                                      className="rounded border-gray-300"
                                    />
                                  </td>
                                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-50">{s.from_unit_name}</td>
                                  <td className="px-3 py-2 text-gray-700">{s.student_name}</td>
                                  <td className="px-3 py-2 text-gray-700">{s.to_unit_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                    {repeaterCandidates.filter(rc => rc.students.length > 0).length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-50 dark:text-gray-50">Wiederholer (in gleicher Klasse/Kurs verbleibend)</h4>
                        <p className="mt-1 text-xs text-gray-500">Schüler:innen, die nicht in die Folgeeinheit wechseln. Mit Häkchen werden sie als Wiederholer geführt (Membership endet am Stichtag, neue Membership in derselben Einheit beginnt).</p>
                        <div className="mt-2 space-y-3">
                          {repeaterCandidates.filter(rc => rc.students.length > 0).map((rc: RepeaterCandidateGroup) => (
                            <div key={`${rc.from_unit_type}-${rc.from_unit_id}`} className="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
                              <p className="font-medium text-gray-800">{rc.from_unit_name}</p>
                              <ul className="mt-2 space-y-1">
                                {rc.students.map((st: RepeaterCandidateStudent) => (
                                  <li key={st.current_membership_id} className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={selectedRepeaterIds.has(st.current_membership_id)}
                                      onChange={() => toggleRepeaterSelection(st.current_membership_id)}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{st.student_name}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row">
                      <button type="button" onClick={() => setCurrentStep(1)} className="btn btn-secondary w-full sm:w-auto">← Zurück</button>
                      <button type="button" onClick={() => { setExecuteResult(null); setCurrentStep(3) }} className="btn btn-primary w-full sm:w-auto">Zur Ausführung</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Schritt 4: Ausführen */}
        {currentStep === 3 && (
          <>
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary-100 p-2">
                <Play className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-50">{STEPS[3].title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Prüfen Sie die Kurzfassung und führen Sie den Wechsel in einem Schritt aus.</p>
              </div>
            </div>
                <div className="mt-6">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-950/80">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <TermHint term="Stichtag" meaning="Ab diesem Datum gelten die neuen Zuordnungen." />: <strong>{transitionDate}</strong>
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      <TermHint term="Übergänge" meaning="Wechsel von einer Klasse/Kurs in die nächste Einheit." />: <strong>{selectedTransitionsForExecute.length}</strong>
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      <TermHint term="Wiederholer" meaning="Lernende bleiben in derselben Klasse oder im selben Kurs." />: <strong>{selectedRepeatersForExecute.length}</strong>
                    </p>
                  </div>
                  {executeResult !== null ? (
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-950/80">
                      <p className="font-medium text-gray-900 dark:text-gray-50">Ergebnis</p>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Beendet: {executeResult.ended} · Neu angelegt: {executeResult.created}
                        {(executeResult.repeatersProcessed ?? 0) > 0 && <> · Wiederholer: {executeResult.repeatersProcessed}</>}
                      </p>
                      {executeResult.errors?.length > 0 && (
                        <p className="mt-2 text-sm text-red-600">{executeResult.errors.length} Fehler</p>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                      <button type="button" onClick={() => setCurrentStep(2)} className="btn btn-secondary w-full sm:w-auto">Zurück zur Vorschau</button>
                      <button
                        type="button"
                        onClick={() => executeBatchMutation.mutate({
                          transition_date: transitionDate,
                          transitions: selectedTransitionsForExecute,
                          repeaters: selectedRepeatersForExecute.length > 0 ? selectedRepeatersForExecute : undefined,
                          school_year_id: selectedSchoolYearId || undefined
                        })}
                        disabled={(selectedTransitionsForExecute.length === 0 && selectedRepeatersForExecute.length === 0) || executeBatchMutation.isLoading}
                        className="btn btn-primary w-full sm:w-auto"
                      >
                        {executeBatchMutation.isLoading ? 'Wird ausgeführt…' : 'Jetzt ausführen'}
                      </button>
                    </div>
                  )}
                </div>
          </>
        )}
      </div>
    </div>
  )
}
